import { PageContainer } from '@/components/page/PageContainer';
import { PageHeader } from '@/components/page/PageHeader';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { TSCard } from '@/components/shared/card/Card';
import { SideMenuOption, VerticalMenu } from '@/components/shared/menu/VerticalMenu';
import { useSystemSettings } from '@/services/settings/SystemSettingsProvider';
import { FC } from 'react';
import { FiAlertTriangle, FiCalendar, FiClipboard, FiClock, FiFileText, FiHome, FiStar } from 'react-icons/fi';
import { GiCog } from 'react-icons/gi';
import { useNavigate, useParams } from 'react-router-dom';
import { NavPage } from '../../components/page/Breadcrumbs';
import strings from './admin.translations';
import AdminContractSection from './contracts/AdminContractSection';
import AdminGeneralSection from './general/AdminGeneralSection';
import { AdminLeaveTypeSection } from './leaveType/AdminLeaveTypeSection';
import AdminLocationsSection from './locations/AdminLocationsSection';
import AdminSkillsSection from './skills/AdminSkillsSection';
import AdminTasksSection from './tasks/AdminTasksSection';
import { useFeatures } from '@/services/backstage/useFeatures';
import { Feature } from '@/services/backstage/Feature';

export const AdminPage: FC = () => {
  const { tabName } = useParams();
  const navigate = useNavigate();

  const advancedLeave = useSystemSettings(state => state.settings.advancedLeaveManagement);

  const breadcrumbs: NavPage[] = [
    {
      name: strings.page.admin,
      to: '/admin'
    }
  ];

  const isAdvancedLeaveEnabled = advancedLeave;

  const canUseRosters = useFeatures('roster');

  const leaveTypesElement = isAdvancedLeaveEnabled ? (
    <AdminLeaveTypeSection isAdvanced={isAdvancedLeaveEnabled} />
  ) : (
    <div className="space-y-3">
      <TSCard className="p-6  flex items-center justify-center">
        <div className="text-center flex flex-col items-center justify-center">
          <FiAlertTriangle className="h-8 w-8 text-amber-400" />
          <p className="mt-4 text-secondary-std font-medium">{strings.state.advancedLeaveDisabled}</p>
        </div>
      </TSCard>
      <AdminLeaveTypeSection isAdvanced={isAdvancedLeaveEnabled} />
    </div>
  );

  const sideMenuOptions: SideMenuOption[] = [
    {
      icon: <GiCog className="h-5 w-5" />,
      label: strings.menu.general,
      anchorId: 'general',
      element: <AdminGeneralSection />
    },
    {
      icon: <FiHome className="h-5 w-5" />,
      label: strings.menu.locations,
      anchorId: 'locations',
      element: <AdminLocationsSection />
    },
    {
      icon: <FiCalendar className="h-5 w-5" />,
      label: strings.menu.leaveTypes,
      anchorId: 'leave-types',
      element: leaveTypesElement,
      disabled: !canUseRosters
    },
    {
      icon: <FiFileText className="h-5 w-5" />,
      label: strings.menu.contracts,
      anchorId: 'contracts',
      element: <AdminContractSection />,
      disabled: !canUseRosters
    },
    {
      icon: <FiStar className="h-5 w-5" />,
      label: strings.menu.skills,
      anchorId: 'skills',
      element: <AdminSkillsSection />,
      disabled: !canUseRosters
    },
    {
      icon: <FiClipboard className="h-5 w-5" />,
      label: strings.menu.tasks,
      anchorId: 'tasks',
      element: <AdminTasksSection />,
      disabled: !canUseRosters
    }
  ];

  const allElements = (
    <>
      <AdminGeneralSection />
      <AdminLocationsSection />
      <Feature name="roster">
        {leaveTypesElement}
        <AdminContractSection />
        <AdminSkillsSection />
        <AdminTasksSection />
      </Feature>
    </>
  );

  const selectedTab = tabName ?? sideMenuOptions[0].anchorId;
  const handleTabSelect = (anchorId: string) => {
    navigate(`/admin/${anchorId}`);
  };

  return (
    <PageContainer
      customHeader={<PageHeader pageTitle={strings.page.title} icon={<FiClock />} breadcrumbs={breadcrumbs} />}
    >
      <PageSizer className="flex gap-5 flex-col lg:flex-row" mode={SizerMode.PAGE}>
        <div className="hidden lg:flex gap-5 w-full">
          <VerticalMenu
            onTabSelect={handleTabSelect}
            sideMenuOptions={sideMenuOptions}
            isScroll={false}
            selectedTab={selectedTab}
          />
        </div>
        <div className="lg:hidden">
          <VerticalMenu
            onTabSelect={handleTabSelect}
            sideMenuOptions={sideMenuOptions}
            allElements={allElements}
            isScroll={true}
            selectedTab={selectedTab}
          />
        </div>
      </PageSizer>
    </PageContainer>
  );
};
